import React, { Suspense, useRef, useEffect } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment } from '@react-three/drei';
import Spinner from './Spinner.js';
import * as THREE from 'three';

function Model({ url, onCameraPositionCalculated }) {
  const { scene: originalScene } = useGLTF(url);
  const { camera, controls } = useThree();
  const groupRef = useRef();
  const clonedSceneRef = useRef(null);

  useEffect(() => {
    if (originalScene && groupRef.current && !clonedSceneRef.current) {
      // Clone the scene ONCE to avoid caching issues
      clonedSceneRef.current = originalScene.clone(true);

      const scene = clonedSceneRef.current;

      // Calculate bounding box from the original scene
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      console.log('Model size:', size);
      console.log('Model center:', center);

      // Center the model
      scene.position.set(-center.x, -center.y, -center.z);

      // Apply scale to the group
      groupRef.current.scale.set(1000, 1000, 1000);

      // Use a fixed camera distance that works well
      const cameraZ = 150; // Fixed distance for better visibility

      console.log('Camera distance:', cameraZ);

      // Position camera
      camera.position.set(0, 0, cameraZ);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();

      if (controls) {
        controls.target.set(0, 0, 0);
        controls.update();
      }

      // Save initial camera position
      if (onCameraPositionCalculated) {
        onCameraPositionCalculated({ x: 0, y: 0, z: cameraZ });
      }

      // Mark as loaded
      setTimeout(() => {
        if (window.setModelLoaded) {
          window.setModelLoaded(false);
        }
      }, 100);
    }
  }, [originalScene, camera, controls, onCameraPositionCalculated]);

  return (
    <group ref={groupRef}>
      {clonedSceneRef.current && <primitive object={clonedSceneRef.current} />}
    </group>
  );
}

function CameraController() {
  const { camera } = useThree();

  const rotate = (direction) => {
    const speed = 0.3;
    if (direction === 'left') camera.position.x -= speed;
    if (direction === 'right') camera.position.x += speed;
    if (direction === 'up') camera.position.y += speed;
    if (direction === 'down') camera.position.y -= speed;
    camera.lookAt(0, 0, 0);
  };

  const zoom = (direction) => {
    const speed = 0.5;
    if (direction === 'in') {
      camera.position.z -= speed;
    } else {
      camera.position.z += speed;
    }
  };

  return { rotate, zoom };
}

export default function Model3DViewer({ modelUrl, width = '100%', height = '600px' }) {
  const controlsRef = useRef();
  const initialCameraPos = useRef({ x: 0, y: 0, z: 15 });
  const [isLoading, setIsLoading] = React.useState(true);

  const handleRotate = (direction) => {
    if (controlsRef.current) {
      const speed = 0.5;
      const controls = controlsRef.current;

      if (direction === 'reset') {
        // Reset camera to initial position
        const pos = initialCameraPos.current;
        controls.object.position.set(pos.x, pos.y, pos.z);
        controls.target.set(0, 0, 0);
        controls.object.lookAt(controls.target);
        controls.update();
        return;
      }

      if (direction === 'left') {
        const offset = controls.object.position.clone().sub(controls.target);
        const phi = Math.atan2(offset.x, offset.z);
        const theta = Math.acos(offset.y / offset.length());

        controls.object.position.x = controls.target.x + offset.length() * Math.sin(theta) * Math.sin(phi + speed);
        controls.object.position.z = controls.target.z + offset.length() * Math.sin(theta) * Math.cos(phi + speed);
      }
      if (direction === 'right') {
        const offset = controls.object.position.clone().sub(controls.target);
        const phi = Math.atan2(offset.x, offset.z);
        const theta = Math.acos(offset.y / offset.length());

        controls.object.position.x = controls.target.x + offset.length() * Math.sin(theta) * Math.sin(phi - speed);
        controls.object.position.z = controls.target.z + offset.length() * Math.sin(theta) * Math.cos(phi - speed);
      }
      if (direction === 'up') {
        const offset = controls.object.position.clone().sub(controls.target);
        const phi = Math.atan2(offset.x, offset.z);
        const theta = Math.acos(offset.y / offset.length());
        const newTheta = Math.max(0.1, Math.min(Math.PI - 0.1, theta - speed));

        controls.object.position.x = controls.target.x + offset.length() * Math.sin(newTheta) * Math.sin(phi);
        controls.object.position.y = controls.target.y + offset.length() * Math.cos(newTheta);
        controls.object.position.z = controls.target.z + offset.length() * Math.sin(newTheta) * Math.cos(phi);
      }
      if (direction === 'down') {
        const offset = controls.object.position.clone().sub(controls.target);
        const phi = Math.atan2(offset.x, offset.z);
        const theta = Math.acos(offset.y / offset.length());
        const newTheta = Math.max(0.1, Math.min(Math.PI - 0.1, theta + speed));

        controls.object.position.x = controls.target.x + offset.length() * Math.sin(newTheta) * Math.sin(phi);
        controls.object.position.y = controls.target.y + offset.length() * Math.cos(newTheta);
        controls.object.position.z = controls.target.z + offset.length() * Math.sin(newTheta) * Math.cos(phi);
      }
      controls.object.lookAt(controls.target);
      controls.update();
    }
  };

  const buttonStyle = {
    width: '36px',
    height: '36px',
    border: '1px solid #ddd',
    background: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    userSelect: 'none',
    transition: 'all 0.2s'
  };

  React.useEffect(() => {
    window.setModelLoaded = setIsLoading;
    return () => {
      delete window.setModelLoaded;
    };
  }, []);

  return (
    <div className="position-relative w-100" style={{ height }}>
      {isLoading && (
        <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 10, background: 'rgba(240, 240, 240, 0.9)' }}>
          <div className="text-center">
            <Spinner />
            <div className="mt-2" style={{ fontSize: '14px', color: '#666' }}>Cargando modelo 3D...</div>
          </div>
        </div>
      )}
      <Canvas
        camera={{ position: [0, 0, 15], fov: 75 }}
        style={{ background: '#f0f0f0' }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.5} />
          <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} />
          <pointLight position={[-10, -10, -10]} intensity={0.5} />
          <Model
            url={modelUrl}
            onCameraPositionCalculated={(pos) => initialCameraPos.current = pos}
          />
          <Environment preset="city" />
          <OrbitControls
            ref={controlsRef}
            enableZoom={true}
            enablePan={true}
            enableRotate={true}
            minDistance={10}
            maxDistance={1000}
          />
        </Suspense>
      </Canvas>

      {/* Control panel - top right */}
      <div className="position-absolute" style={{ top: '10px', right: '10px' }}>
        <div className="bg-white shadow-sm rounded p-2">
          {/* Rotation controls */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{ textAlign: 'center', fontSize: '11px', color: '#666', marginBottom: '4px' }}>Rotar</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 36px)', gap: '2px' }}>
              <div></div>
              <button style={buttonStyle} onClick={() => handleRotate('up')} onMouseEnter={(e) => e.target.style.background = '#f0f0f0'} onMouseLeave={(e) => e.target.style.background = 'white'}>↑</button>
              <div></div>
              <button style={buttonStyle} onClick={() => handleRotate('left')} onMouseEnter={(e) => e.target.style.background = '#f0f0f0'} onMouseLeave={(e) => e.target.style.background = 'white'}>←</button>
              <button style={{ ...buttonStyle, background: '#f8f8f8', cursor: 'pointer' }} onClick={() => handleRotate('reset')} onMouseEnter={(e) => e.target.style.background = '#e8e8e8'} onMouseLeave={(e) => e.target.style.background = '#f8f8f8'}>↻</button>
              <button style={buttonStyle} onClick={() => handleRotate('right')} onMouseEnter={(e) => e.target.style.background = '#f0f0f0'} onMouseLeave={(e) => e.target.style.background = 'white'}>→</button>
              <div></div>
              <button style={buttonStyle} onClick={() => handleRotate('down')} onMouseEnter={(e) => e.target.style.background = '#f0f0f0'} onMouseLeave={(e) => e.target.style.background = 'white'}>↓</button>
              <div></div>
            </div>
          </div>

          {/* Zoom controls */}
          <div>
            <div style={{ textAlign: 'center', fontSize: '11px', color: '#666', marginBottom: '4px' }}>Zoom</div>
            <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
              <button
                style={buttonStyle}
                onClick={() => controlsRef.current && (controlsRef.current.dollyOut(1.2))}
                onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >⊕</button>
              <button
                style={buttonStyle}
                onClick={() => controlsRef.current && (controlsRef.current.dollyIn(1.2))}
                onMouseEnter={(e) => e.target.style.background = '#f0f0f0'}
                onMouseLeave={(e) => e.target.style.background = 'white'}
              >⊖</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
